import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useState, useEffect, useCallback } from "react";
import { LinearProgress, CircularProgress } from "@mui/material";
import { useGeneratedQuiz } from "../../hooks/useQuiz";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import * as styles from "./quizStyles";
import { CorrectAnswer } from "../../types/Quiz";

const Quiz = () => {
    const { data: quizQuestions, isLoading, refetch } = useGeneratedQuiz();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [answers, setAnswers] = useState<
        { question: string; correct: boolean }[]
    >([]);
    const [showSummary, setShowSummary] = useState(false);

    const generateQuiz = useCallback(() => {
        refetch();
        setCurrentQuestionIndex(0);
        setProgress(0);
        setAnswers([]);
        setShowSummary(false);
    }, []);

    useEffect(() => {
        if (quizQuestions && quizQuestions.length > 0) {
            setCurrentQuestionIndex(0);
            setProgress(0);
            setAnswers([]);
            setShowSummary(false);
        }
    }, []);

    const currentQuestion = quizQuestions[currentQuestionIndex];

    const handleAnswerClick = (answer: CorrectAnswer) => {
        if (!currentQuestion) return;

        setAnswers((prev) => [
            ...prev,
            {
                question: currentQuestion.question,
                correct: answer === currentQuestion.correct_answer,
            },
        ]);

        setProgress(((currentQuestionIndex + 1) / 8) * 100);

        if (currentQuestionIndex < quizQuestions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        } else {
            setShowSummary(true);
        }
    };

    if (showSummary) {
        const correctCount = answers.filter((a) => a.correct).length;
        return (
            <Box sx={styles.homeContainer}>
                <Header />
                <Box sx={styles.summaryBox}>
                    <h2>Quiz Zusammenfassung</h2>
                    <p>
                        {correctCount} von {answers.length} Fragen waren
                        korrekt.
                    </p>
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={generateQuiz}
                    >
                        Nächstes Quiz
                    </Button>
                </Box>
                <Footer />
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={styles.homeContainer}>
                <Header />
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
                <Footer />
            </Box>
        );
    }

    if (!currentQuestion) return null;

    return (
        <Box sx={styles.homeContainer}>
            <Header />
            <Box>
                <Box component="section" sx={styles.questionBox}>
                    {currentQuestion.question}
                </Box>
                <Grid container spacing={1} sx={styles.answersGrid}>
                    {(["A", "B", "C", "D"] as CorrectAnswer[]).map((key) => (
                        <Grid item xs={6} key={key}>
                            <Button
                                variant="contained"
                                onClick={() => handleAnswerClick(key)}
                                sx={styles.answerButton}
                            >
                                {currentQuestion[key]}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={styles.progressBar}
                />
            </Box>
            <Footer />
        </Box>
    );
};

export default Quiz;
